// src/pages/Partners.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Col, Container, Row, Button, Spinner, Alert } from "reactstrap";
import PartnerList from "../components/Partner/PartnerList";
import NewPartnerModal from "../components/Partner/NewPartnerModal";
import axios from "axios";
import { API_URL_partners } from "../constants";
import { getAuthHeaders } from "../utils/auth";
import Moment from "moment";
import Papa from "papaparse";
import { formatDate } from "../components/utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// CSV Utilities
const CsvUtils = {
  downloadWithBOM: (data, filename, delimiter = ",") => {
    if (!data?.length) {
      toast.info("No hay datos para exportar.");
      return;
    }

    const csv = Papa.unparse(data, {
      delimiter,
      quotes: true,
      newline: "\r\n",
      header: true,
    });

    const csvWithBOM = "\uFEFF" + csv;
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  validateFile: (file) => {
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ["text/csv", "application/vnd.ms-excel"];

    if (!file) {
      return { valid: false, error: "No se seleccionó archivo" };
    }

    if (file.size > MAX_SIZE) {
      return {
        valid: false,
        error: "El archivo es demasiado grande (máx 10MB)",
      };
    }

    if (!ALLOWED_TYPES.includes(file.type) && !file.name.endsWith(".csv")) {
      return { valid: false, error: "Solo se permiten archivos CSV" };
    }

    return { valid: true };
  },

  formatDateForExport: (value) => {
    if (!value) return "";
    const m = Moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD") : value;
  },
};

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef(null);

  // Fetch partners
  const getPartners = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const headers = getAuthHeaders();

    try {
      const response = await axios.get(API_URL_partners, {
        headers,
        timeout: 10000,
      });

      setPartners(response.data || []);
      setFilteredPartners([]);
    } catch (err) {
      console.error("Failed to fetch partners:", err);

      const errorMsg =
        err.response?.status === 401
          ? "Sesión expirada. Por favor inicia sesión nuevamente."
          : err.response?.data?.message || "Error al cargar equipo";

      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    getPartners();
  }, [getPartners]);

  const resetState = useCallback(() => {
    getPartners();
  }, [getPartners]);

  const handleFilteredChange = useCallback((filteredRows) => {
    setFilteredPartners(filteredRows || []);
  }, []);

  // Download CSV
  const downloadCSV = useCallback(() => {
    const source = filteredPartners.length ? filteredPartners : partners;

    if (!source.length) {
      toast.info("No hay miembros para exportar.");
      return;
    }

    const exportRows = source.map((p) => ({
      "Codigo de Miembro": p.partner_code ?? "",
      Miembro: p.partner ?? "",
      Nivel: p.nivel ?? "",
      "Correo de Miembro": p.email ?? "",
      "Value Stream": p.grupo ?? "",
      Localizacion: p.localizacion ?? "",
      IVA: p.iva ?? "",
      "Retencion IVA": p.retencion ?? "",
      "Retencion ISR/IRPF": p.isr ?? "",
      Status: p.status ?? "Activo", // Include status
      "Fecha de Ingreso": CsvUtils.formatDateForExport(p.ingreso_date),
      "Fecha de Cumpleaños": CsvUtils.formatDateForExport(p.cumple_date),
      "Fecha de Registro": CsvUtils.formatDateForExport(p.registration_date),
    }));

    const dateStr = new Date().toISOString().slice(0, 10);
    CsvUtils.downloadWithBOM(exportRows, `Equipo_${dateStr}.csv`);

    toast.success("CSV descargado exitosamente");
  }, [partners, filteredPartners]);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) {
      setFile(null);
      return;
    }

    const validation = CsvUtils.validateFile(selectedFile);

    if (!validation.valid) {
      toast.error(validation.error);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFile(selectedFile);
  }, []);

  // Update database from CSV
  const updateDatabase = useCallback(
    async (result) => {
      const data = (result?.data || []).map((item) => ({
        partner_code:
          item.partner_code ??
          item["Codigo de Miembro"] ??
          item["Codigo de miembro"] ??
          "",
        partner: item.partner ?? item["Miembro"] ?? "",
        nivel: item.nivel ?? item["Nivel"] ?? "",
        email: item.email ?? item["Correo de Miembro"] ?? "",
        grupo: item.grupo ?? item["Value Stream"] ?? "Default Value",
        localizacion: item.localizacion ?? item["Localizacion"] ?? "",
        iva: item.iva ?? item["IVA"] ?? "",
        retencion: item.retencion ?? item["Retencion IVA"] ?? "",
        isr: item.isr ?? item["Retencion ISR/IRPF"] ?? "",
        status: item.status ?? item["Status"] ?? "Activo", // Handle status
        ingreso_date: formatDate(
          item.ingreso_date ?? item["Fecha de Ingreso"]
        ),
        cumple_date: formatDate(
          item.cumple_date ?? item["Fecha de Cumpleaños"]
        ),
        registration_date: formatDate(
          item.registration_date ?? item["Fecha de Registro"]
        ),
      }));

      if (!data.length) {
        toast.warn("El CSV parece vacío.");
        return;
      }

      const headers = getAuthHeaders();
      const duplicates = [];
      const otherErrors = [];
      let successCount = 0;

      for (const [index, entry] of data.entries()) {
        try {
          await axios.post(API_URL_partners, entry, { headers });
          successCount++;
        } catch (err) {
          const errData = err?.response?.data || {};

          const isDuplicate =
            errData.partner_code?.some?.((msg) =>
              msg.toLowerCase().includes("already exists")
            ) ||
            errData.email?.some?.((msg) =>
              msg.toLowerCase().includes("already exists")
            );

          const rowInfo = {
            csvRow: index + 2,
            partner_code: entry.partner_code,
            partner: entry.partner,
            email: entry.email,
            error: errData,
          };

          if (isDuplicate) {
            duplicates.push(rowInfo);
            console.warn("Duplicate detected in CSV row", rowInfo.csvRow, rowInfo);
          } else {
            otherErrors.push(rowInfo);
            console.error("Error creating partner from CSV row", rowInfo.csvRow, rowInfo);
          }
        }
      }

      // Summary
      if (successCount > 0) {
        toast.success(`✓ ${successCount} miembro(s) cargado(s) exitosamente`);
        resetState();
      }

      if (duplicates.length > 0) {
        console.table(duplicates);
        toast.warn(
          `⚠ ${duplicates.length} duplicado(s) encontrado(s) (ver consola)`
        );
      }

      if (otherErrors.length > 0) {
        console.error("CSV import errors:", otherErrors);
        toast.error(`✗ ${otherErrors.length} error(es) al cargar (ver consola)`);
      }

      // Clear file input
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [resetState]
  );

  // Handle file upload
  const handleFileUpload = useCallback(() => {
    if (!file) {
      toast.warn("Selecciona un archivo CSV primero");
      return;
    }

    setIsUploading(true);

    const reader = new FileReader();

    reader.onload = (event) => {
      Papa.parse(event.target.result, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          updateDatabase(result);
          setIsUploading(false);
        },
        error: (err) => {
          console.error("Papa parse error:", err);
          toast.error("Error al leer el CSV");
          setIsUploading(false);
        },
      });
    };

    reader.onerror = () => {
      console.error("FileReader error:", reader.error);
      toast.error("Error al leer el archivo");
      setIsUploading(false);
    };

    reader.onabort = () => {
      toast.error("Lectura de archivo cancelada");
      setIsUploading(false);
    };

    reader.readAsText(file);
  }, [file, updateDatabase]);

  return (
    <Container style={{ marginTop: "20px" }}>
      <h1>Equipo</h1>

      {error && (
        <Alert color="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Row className="mb-3">
        <Col className="d-flex gap-2 align-items-center flex-wrap">
          <Button
            color="info"
            onClick={downloadCSV}
            disabled={isLoading || !partners.length}
            aria-label="Descargar equipo en formato CSV"
          >
            📥 Descargar CSV
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isUploading}
            aria-label="Seleccionar archivo CSV para importar"
          />

          <Button
            color="primary"
            onClick={handleFileUpload}
            disabled={!file || isUploading}
            aria-label="Subir archivo CSV seleccionado"
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Subiendo...
              </>
            ) : (
              "📤 Subir CSV"
            )}
          </Button>

          {file && (
            <small className="text-muted">
              Archivo: {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </small>
          )}
        </Col>
      </Row>

      <hr />

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Cargando equipo...</p>
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <PartnerList
                partners={partners}
                resetState={resetState}
                itemsPerPage={8}
                onFilteredChange={handleFilteredChange}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <NewPartnerModal create={true} resetState={resetState} />
            </Col>
          </Row>
        </>
      )}

      <ToastContainer position="bottom-right" autoClose={4000} />
    </Container>
  );
};

export default Partners;